<template>
  <VLoader v-if="loading" classes="bg-white" />
  <div v-else>
    <div class="shared-table-container">
      <div class="shared-table-toolbar">
        <h3 class="shared-table-toolbar-heading">Referral Report</h3>
        <TooltipDateRange v-if="withTooltip" @onSelect="handleDateRangeSelect" />
        <div class="ml-auto">
          <slot name="table-toolbar"></slot>
        </div>
      </div>

      <div class="shared-table-innermost-container">
        <table class="shared-table">
          <thead>
            <tr class="shared-table-headings" :style="{ ...styles }">
              <!-- Leftmost th belonging to expand button -->
              <th class="shared-table-headings-heading"></th>

              <!-- ths defined in the tColumns prop -->
              <th
                v-for="({ column }, index) in tColumns"
                :key="`heading-${index}`"
                class="px-2 cursor-auto shared-table-headings-heading"
              >
                <div class="flex items-center justify-center">
                  {{ column }}
                </div>
              </th>

              <!-- Rightmost th for the actions buttons -->
              <th v-if="withActionsCol" class="shared-table-headings-heading">actions</th>
            </tr>
          </thead>
          <tbody>
            <!-- #1 Show on empty table -->
            <template v-if="!tData.data.length">
              <tr class="shared-table-cells">
                <td :colspan="tColumns.length" class="shared-table-cells-cell">
                  <div class="py-10 text-center uppercase">
                    {{ toolbarHeading ? `-- the ${toolbarHeading} is empty --` : `-- empty --` }}
                  </div>
                </td>
              </tr>
            </template>

            <template v-else>
              <ReferralsTableRow
                v-for="(data, index) in tData.data"
                :key="`referrers-data-${index}`"
                :trData="data"
                :tColumns="tColumns"
                :tNestedData="normalizeAdminReferrals(tNestedData[data.id])"
                :tNestedColumns="tNestedColumns"
                :referralsAll="tNestedData"
                :getReferrals="getReferrals(data.id)"
                :styles="{ ...styles }"
                :withActionsCol="withActionsCol"
                @select="items => handleOrderDetails(items)"
              >
                <template v-if="withActionsCol" #table-data-actions="tData">
                  <slot name="table-data-actions" v-bind="tData"></slot>
                </template>
              </ReferralsTableRow>
            </template>
          </tbody>
        </table>
      </div>
    </div>

    <div class="flex items-center justify-center">
      <Pagination
        :totalPages="tData.meta ? tData.meta.pagination.total_pages : 1"
        :currentPage="tData.meta ? tData.meta.pagination.current_page : 1"
        @onPaginate="handlePaginate"
      />
    </div>

    <ModalReferralOrderDetails
      :show="showModal"
      :tableData="orderDetails"
      :tableHeadings="$options.tOrderDetailsColumns"
      @close="handleCloseReferralModal"
    />
  </div>
</template>

<script>
import { normalizeAdminReferrals } from '@/utils';

import Pagination from '@/components/shared/Pagination';
import TooltipDateRange from '@/components/shared/tooltip/TooltipDateRange';
import ModalReferralOrderDetails from '@/components/shared/modal/ModalReferralOrderDetails';
import ReferralsTableRow from '@/components/admin/ReferralsTableRow';
import VLoader from '@/components/base/VLoader';

export default {
  name: 'AdminReferralsTable',
  components: {
    Pagination,
    TooltipDateRange,
    ModalReferralOrderDetails,
    ReferralsTableRow,
    VLoader,
  },
  props: {
    toolbarHeading: {
      type: String,
      required: false,
      default: 'referrals',
    },
    tData: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    tColumns: {
      type: Array,
      required: false,
      default: () => [],
    },
    tNestedColumns: {
      type: Array,
      required: false,
      default: () => [],
    },
    tNestedData: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    getTData: {
      type: Function,
      required: false,
      default: () => {},
    },
    getReferrals: {
      type: Function,
      required: true,
      default: () => {},
    },
    styles: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    withActionsCol: {
      type: Boolean,
      required: false,
      default: false,
    },
    withTooltip: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      dateRanges: ['', ''],
      showModal: false,
      orderDetails: [],
      selectedPayout: {},
      csvData: [],
      loading: false,
    };
  },
  methods: {
    normalizeAdminReferrals: data => normalizeAdminReferrals(data),
    handleOrderDetails(items) {
      this.orderDetails = items;
      this.showModal = true;
    },
    handleOpenReferralModal(data) {
      this.showModal = true;
      this.selectedPayout = data;
    },
    handleCloseReferralModal() {
      this.showModal = false;
      this.selectedPayout = {};
    },
    /*
     * I'm going to duplicate these functions from the Table component
     * until I come up with a better solution to keep it DRY (Maybe a mixin)
     */
    async handlePaginate(page) {
      const start_date = this.dateRanges[0];
      const end_date = this.dateRanges[1];

      const params = !(start_date && end_date) ? { page } : { page, start_date, end_date };
      await this.getTData(params);
    },
    async handleDateRangeSelect(dateRanges) {
      const start_date = dateRanges[0];
      const end_date = dateRanges[1];
      const params = !(start_date && end_date) ? { page: 1 } : { start_date, end_date };

      await this.getTData(params);
      this.dateRanges = dateRanges;
    },
  },
  async mounted() {
    this.loading = true;
    await this.getTData();
    this.loading = false;
  },
  tOrderDetailsColumns: [],
  async created() {
    /*
     * Order Details/Items Table Columns
     */
    const TABLE_ODER_DETAILS_COLUMNS = [
      'Item Name',
      'Serial Number #',
      'Chroma Tier',
      'Quantity',
      'Amount',
      'Date',
    ];
    this.$options.tOrderDetailsColumns = TABLE_ODER_DETAILS_COLUMNS;
  },
};
</script>
